import React, { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { NotificationMessage } from '@/components';
import { BlankLayout, StatusLayout } from '@/layout';
import { makeSelectWallets } from '@/model/wallets/selectors';
import { init } from '@/model/withdrawal/actions';
import { makeSelectInitialized } from '@/model/withdrawal/selectors';
import { withDictionaryInitialization } from '@/pages/dictionary/hocs';
import { withWalletsInitialization } from '@/pages/wallets/hocs';

const selectInitialized = makeSelectInitialized();
const selectWallets = makeSelectWallets();

const withWithdrawalInitialization = <T extends PropsWithChildren<unknown>>(checkWallets?: boolean) => (
  Component: React.FC<T>,
) => (props: T): React.ReactElement<T> => {
  const dispatch = useDispatch();
  const { selected, all } = useSelector(selectWallets);
  const initializing = useRef<boolean>(false);
  const initialized = useSelector(selectInitialized);
  useEffect(() => {
    if (!initialized && !initializing.current) {
      initializing.current = true;
      (async () => {
        try {
          await init()(dispatch);
        } finally {
          initializing.current = false;
        }
      })();
    }
  }, [dispatch, initialized, initializing]);
  const selectedWalletIsValid = useMemo(() => {
    if (!checkWallets) {
      return true;
    }
    const foundWallet = all.find(({ id }) => selected === id);
    return !!foundWallet;
  }, [all, selected]);
  if (!initialized) {
    return <BlankLayout />;
  }
  if (!selectedWalletIsValid) {
    return (
      <StatusLayout status="failed" height={220}>
        <NotificationMessage severity="error" title={<FormattedMessage id="withdrawal.errors.invalid-wallet.title" />}>
          <FormattedMessage id="withdrawal.errors.invalid-wallet.content" />
        </NotificationMessage>
      </StatusLayout>
    );
  }

  return <Component {...props} />;
};

export default <T extends PropsWithChildren<unknown>>(checkWallets?: boolean) => (
  Component: React.FC<T>,
): React.FC<T> =>
  withDictionaryInitialization(withWalletsInitialization(withWithdrawalInitialization<T>(checkWallets)(Component)));
